interface HubSpotFormHandle {
  getInstanceId: () => string
  setFieldValue: (name: string, value: unknown) => void
}

declare global {
  interface Window {
    HubSpotFormsV4?: {
      getForms: () => HubSpotFormHandle[]
      getFormFromEvent: (event: Event) => HubSpotFormHandle | undefined
    }
  }
}

/** Bubbles to `window`; `detail.instanceId` is the container element's id. */
const READY_EVENT = 'hs-form-event:on-ready'

/**
 * Field names carry an object-type prefix (`0-1/email`) that varies per form,
 * so a logical name is matched against the rendered input instead.
 */
function resolveFieldName(container: HTMLElement, logical: string): string | undefined {
  const input = container.querySelector<HTMLInputElement>(
    `[name="${logical}"], [name$="/${logical}"]`,
  )
  return input?.name
}

/**
 * Seed fields on a form rendered by the developer embed, keyed by logical
 * field name. Values have to go through the embed's own handle: the `hsfc-*`
 * markup is a React tree that overwrites anything written onto the input.
 */
export function useHubSpotFormPrefill(
  containerId: string,
  values: () => Record<string, string | undefined>,
) {
  const form = shallowRef<HubSpotFormHandle>()

  function applyPrefill() {
    if (!form.value)
      return

    const container = document.getElementById(containerId)
    if (!container)
      return

    for (const [logical, value] of Object.entries(values())) {
      if (!value)
        continue

      const name = resolveFieldName(container, logical)
      if (!name)
        continue

      try {
        form.value.setFieldValue(name, value)
      }
      catch {
        // field dropped from the form in HubSpot; leave it blank
      }
    }
  }

  function onReady(event: Event) {
    const { instanceId } = (event as CustomEvent<{ instanceId?: string }>).detail ?? {}
    if (instanceId !== containerId)
      return

    form.value = window.HubSpotFormsV4?.getFormFromEvent(event)
    applyPrefill()
  }

  onMounted(() => {
    window.addEventListener(READY_EVENT, onReady)

    // the embed can finish rendering before the listener is attached
    form.value = window.HubSpotFormsV4?.getForms().find(f => f.getInstanceId() === containerId)
    applyPrefill()
  })

  onBeforeUnmount(() => window.removeEventListener(READY_EVENT, onReady))

  watch(values, applyPrefill, { deep: true })
}
